import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Package, LogIn } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const initials = (name = "", email = "") => {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

const ProfileMenu = () => {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();

  if (!customer) {
    return (
      <Link
        to="/login"
        data-testid="profile-signin"
        className="flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white text-[var(--text-primary)] transition-colors hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
        aria-label="Sign in"
      >
        <User size={18} />
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="profile-menu-trigger"
          className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-black/15 bg-[var(--brand-primary)] text-sm font-semibold text-white transition-transform hover:scale-105"
          aria-label="Account"
        >
          {customer.picture ? (
            <img src={customer.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            initials(customer.name, customer.email)
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white">
        <DropdownMenuLabel className="truncate">
          <div className="font-semibold">{customer.name || "Account"}</div>
          <div className="truncate text-xs font-normal text-[var(--text-secondary)]">{customer.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-my-orders" className="cursor-pointer">
          <Package size={15} className="mr-2" /> My Orders
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/order")} className="cursor-pointer">
          <LogIn size={15} className="mr-2" /> Place an Order
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="menu-signout"
          className="cursor-pointer text-red-600 focus:text-red-600"
        >
          <LogOut size={15} className="mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const nav = [
    { to: "/", label: "Home" },
    { to: "/order", label: "Place an Order" },
    { to: "/contact", label: "Contact" },
    { to: "/admin", label: "Admin" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-black/10 bg-[#F9F9F8]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="NLCS logo" className="h-11 w-11 object-contain" />
          <span className="leading-tight">
            <span className="block font-display text-xl font-extrabold tracking-tight text-[var(--text-primary)]">
              NLCS
            </span>
            <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Victoria Road
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-2">
          <nav className="hidden items-center gap-1 md:flex">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-sm font-medium transition-colors hover:text-[var(--brand-primary)] ${
                  pathname === n.to ? "text-[var(--brand-primary)]" : "text-[var(--text-primary)]"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <ProfileMenu />

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-black/10 bg-white md:hidden">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="block px-6 py-3 text-sm font-medium hover:bg-black/5"
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
